import { useEffect, useRef, useState } from 'react';
import { analyticsService } from '../services/analyticsService';
import { feedbackService } from '../services/feedbackService';
import { todoService } from '../services/todoService';
import { useAuth } from '../context/AuthContext';

const DEFAULTS = { focus: 25, shortBreak: 5, longBreak: 15, longEvery: 4 };

export default function useTimer() {
  const { user } = useAuth?.() || { user: null };
  const [settings, setSettings] = useState(DEFAULTS);
  const [mode, setMode] = useState('focus'); // 'focus' | 'short' | 'long'
  const [round, setRound] = useState(1);
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(settings.focus * 60);
  const [attachedTodo, setAttachedTodo] = useState(null); // {id,title} | null
  const tickRef = useRef(null);
  // Session timing for procrastination metric
  const sessionStartRef = useRef(null); // Date
  const sessionEndRef = useRef(null); // Date
  const [procrastinatedTime, setProcrastinatedTime] = useState(0); // seconds
  // Wake Lock (keep screen awake during focus)
  const wakeLockRef = useRef(null);
  // Last timer event for UI effects (confetti, etc.)
  const [lastEvent, setLastEvent] = useState(null); // { type: 'focus_complete'|'break_complete', at: Date }

  // persist remaining/mode between reloads (basic MVP)
  useEffect(() => {
    const saved = localStorage.getItem('fmp_timer');
    if (saved) {
      try {
        const v = JSON.parse(saved);
        setMode(v.mode ?? 'focus');
        setRound(v.round ?? 1);
        setRemaining(v.remaining ?? (v.settings?.focus ?? DEFAULTS.focus) * 60);
        setSettings(v.settings ?? DEFAULTS);
        // Restore session timing if present
        if (v.sessionStart) sessionStartRef.current = new Date(v.sessionStart);
        if (v.sessionEnd) sessionEndRef.current = new Date(v.sessionEnd);
        if (v.procrastinatedTime) setProcrastinatedTime(v.procrastinatedTime);
        if (v.attachedTodo) setAttachedTodo(v.attachedTodo);
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('fmp_timer', JSON.stringify({
      mode,
      round,
      remaining,
      settings,
      sessionStart: sessionStartRef.current ? sessionStartRef.current.toISOString() : null,
      sessionEnd: sessionEndRef.current ? sessionEndRef.current.toISOString() : null,
      procrastinatedTime,
      attachedTodo,
    }));
  }, [mode, round, remaining, procrastinatedTime, settings, attachedTodo]);
  // Update timer duration if settings change and timer is not running
  useEffect(() => {
    if (!running) {
      setRemaining(getDuration(mode) * 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.focus, settings.shortBreak, settings.longBreak, settings.longEvery]);

  useEffect(() => {
    if (!running) return;
    tickRef.current = setInterval(() => {
      setRemaining((s) => {
        if (s <= 1) {
          clearInterval(tickRef.current);
          setRunning(false);
          onComplete();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  // Manage Wake Lock while focusing
  useEffect(() => {
    async function requestWakeLock() {
      try {
        if ('wakeLock' in navigator && !wakeLockRef.current) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          wakeLockRef.current.addEventListener?.('release', () => {
            wakeLockRef.current = null;
          });
        }
      } catch {}
    }
    function releaseWakeLock() {
      try {
        if (wakeLockRef.current?.release) {
          wakeLockRef.current.release();
        }
      } catch {}
      wakeLockRef.current = null;
    }
    if (running && mode === 'focus') requestWakeLock();
    else releaseWakeLock();

    const handleVis = () => {
      if (document.visibilityState === 'visible' && running && mode === 'focus') {
        // try to re-acquire
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVis);
    return () => {
      document.removeEventListener('visibilitychange', handleVis);
    };
  }, [running, mode]);

  const start = () => {
    // If starting first focus round, record session start
    if (mode === 'focus' && round === 1 && !sessionStartRef.current) {
      sessionStartRef.current = new Date();
    }
    // feedback on start
    feedbackService.playBeep(120, 640, 0.06);
    setRunning(true);
  };
  const pause = () => setRunning(false);
  const reset = () => {
    setRunning(false);
    setRemaining(getDuration(mode) * 60);
  };
  const skip = () => nextInterval();
  const adjustRemaining = (deltaMinutes) => {
    setRemaining((s) => Math.max(0, s + deltaMinutes * 60));
  };

  function getDuration(m) {
    if (m === 'focus') return settings.focus;
    if (m === 'short') return settings.shortBreak;
    return settings.longBreak;
  }

  async function nextInterval() {
    if (mode === 'focus') {
      const nextRound = round % settings.longEvery === 0 ? 'long' : 'short';
      setMode(nextRound);
      setRemaining(getDuration(nextRound) * 60);
    } else {
      // If finishing last round (long break), record session end
      const isFinalRound = mode === 'long';
      if (isFinalRound && sessionStartRef.current) {
        sessionEndRef.current = new Date();
        // Calculate procrastinated time
        const actualElapsed = (sessionEndRef.current - sessionStartRef.current) / 1000; // seconds
        const estimated = settings.focus * settings.longEvery * 60; // seconds
        const procrastinated = actualElapsed - estimated;
        setProcrastinatedTime(procrastinated);
        // Log to analytics
        analyticsService.logTimerEvent({
          type: 'session_complete',
          mode: 'focus',
          rounds: settings.longEvery,
          estimated,
          actualElapsed,
          procrastinated,
          at: new Date(),
        });
      }
      // Reset session timing for next session
      sessionStartRef.current = null;
      sessionEndRef.current = null;
      setMode('focus');
      setRound(1);
      setRemaining(settings.focus * 60);
    }
  }

  function onComplete() {
    // Feedback and bookkeeping when interval ends
    feedbackService.playBeep(160, 880, 0.08);
    feedbackService.vibrate(100);
    // If a focus interval just completed, mark event, update attached todo actualPomos
    if (mode === 'focus') {
      setLastEvent({ type: 'focus_complete', at: new Date() });
      if (user && attachedTodo?.id) {
        todoService.incrementActualPomos(user.uid, attachedTodo.id, 1);
      }
    } else {
      setLastEvent({ type: 'break_complete', at: new Date() });
    }
    nextInterval();
  }

  return {
    state: { mode, round, running, remaining, settings, procrastinatedTime, lastEvent, attachedTodo },
    start, pause, reset, skip, setSettings, setAttachedTodo, adjustRemaining,
  };
}
